/**
 * The assistant's conversation (this session, in memory): what the panel
 * shows, and the API messages sent back to Claude each turn.
 *
 * Proposed actions (Fase 6c) appear as cards. Confirming runs the action and
 * logs it (undoable); declining does nothing. The outcomes go back to Claude
 * with the next question, without an extra request, unless Claude asked to
 * continue right after (continue_after).
 */
import { create } from 'zustand';
import {
  AiError,
  contextText,
  DECLINED,
  NOT_DECIDED,
  outcome,
  runTurn,
  toolContext,
  undoAction,
  type ActionPreview,
  type AiErrorCode,
  type AiUsage,
  type CheckedAction,
  type ContentBlockParam,
  type MessageParam,
  type Undo,
} from '@nexus/ai';
import type { Store } from '@nexus/store';
import { useUi } from '../state/ui';
import { transport } from './providers';

export type CardStatus =
  'pending' | 'running' | 'done' | 'declined' | 'failed' | 'skipped' | 'undone';

export interface ActionCard {
  callId: string;
  preview: ActionPreview;
  status: CardStatus;
  continueAfter: boolean;
  activityId?: string;
  error?: string;
}

export interface ChatItem {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  /** Pages Claude read for this answer. */
  sources?: string[];
  /** A tool is running ("searching your notes…"). */
  tool?: string | null;
  error?: AiErrorCode;
  pending?: boolean;
  actions?: ActionCard[];
}

/** Turns a preview into the log line ("Taak 'X' in Inbox"), in the app's language. */
export type Describe = (preview: ActionPreview) => string;

interface ChatState {
  items: ChatItem[];
  /** The API conversation; only complete turns (failed turns are dropped). */
  messages: MessageParam[];
  /** Tool results that go back to Claude with the next message. */
  carry: ContentBlockParam[];
  running: boolean;
  usage: AiUsage | null;
  abort: AbortController | null;
  send: (store: Store, question: string, language: string) => Promise<void>;
  decide: (
    store: Store,
    callId: string,
    confirm: boolean,
    describe: Describe,
    language: string,
  ) => Promise<void>;
  undo: (store: Store, callId: string) => Promise<boolean>;
  stop: () => void;
  reset: () => void;
}

export { transport } from './providers';

/** The main places, so Claude knows where tasks and meeting notes go. */
async function places(store: Store, visible: (id: string) => boolean) {
  const named: [string, string][] = [
    ['Taken', 'tasks_parent_id'],
    ['Projecten', 'projects_db_id'],
    ['Vergaderingen', 'meetings_db_id'],
    ['Inbox', 'inbox_page_id'],
  ];
  const out: { name: string; id: string }[] = [];
  for (const [name, key] of named) {
    const id = await store.getSetting(key);
    if (id && visible(id)) out.push({ name, id });
  }
  return out;
}

let nextId = 1;
/** Checked actions waiting for a decision (functions: kept outside the store). */
const proposals = new Map<string, CheckedAction>();
/** Undo data per confirmed card. */
const undos = new Map<string, { undo: Undo; activityId: string }>();

export const useAiChat = create<ChatState>((set, get) => {
  const patchCard = (callId: string, p: Partial<ActionCard>) =>
    set((s) => ({
      items: s.items.map((i) =>
        i.actions?.some((a) => a.callId === callId)
          ? { ...i, actions: i.actions.map((a) => (a.callId === callId ? { ...a, ...p } : a)) }
          : i,
      ),
    }));

  /** Run one turn: a new question, or (question null) the outcomes after continue_after. */
  const turn = async (store: Store, language: string, question: string | null) => {
    const abort = new AbortController();
    const answerId = nextId + 1;
    nextId += 2;
    // Cards nobody decided on: not done.
    const undecided = get().items.flatMap((i) =>
      (i.actions ?? []).filter((a) => a.status === 'pending'),
    );
    for (const a of undecided) {
      patchCard(a.callId, { status: 'skipped' });
      proposals.delete(a.callId);
    }
    const carry = [...get().carry, ...undecided.map((a) => outcome(a.callId, NOT_DECIDED))];
    set((s) => ({
      running: true,
      abort,
      carry: [],
      items: [
        ...s.items,
        ...(question ? [{ id: answerId - 1, role: 'user' as const, text: question }] : []),
        { id: answerId, role: 'assistant', text: '', pending: true, tool: null },
      ],
    }));
    const patch = (p: Partial<ChatItem>) =>
      set((s) => ({ items: s.items.map((i) => (i.id === answerId ? { ...i, ...p } : i)) }));

    try {
      const ctx = await toolContext(store);
      const { tabs, active } = useUi.getState();
      const tab = tabs[active];
      // The open page, unless it is hidden from the AI.
      const page = tab?.kind === 'page' ? ctx.scope.visible.get(tab.pageId) : undefined;
      const content: ContentBlockParam[] = [...carry];
      if (question) {
        const where = await places(store, (id) => ctx.scope.visible.has(id));
        content.push(
          { type: 'text', text: contextText({ now: new Date(), language, page, places: where }) },
          { type: 'text', text: question },
        );
      }
      let streamed = '';
      const result = await runTurn({
        transport,
        store,
        model: 'sonnet', // the route for the task decides (providers.ts)
        messages: [...get().messages, { role: 'user', content }],
        signal: abort.signal,
        context: ctx,
        callbacks: {
          onText: (t) => {
            streamed += t;
            patch({ text: streamed, tool: null });
          },
          onTool: (name) => patch({ tool: name }),
          onUsage: (usage) => set({ usage }),
        },
      });
      const cards: ActionCard[] = (result.pending?.proposals ?? []).map((p) => {
        proposals.set(p.callId, p.action);
        return {
          callId: p.callId,
          preview: p.action.preview,
          status: 'pending',
          continueAfter: p.continueAfter,
        };
      });
      set({ messages: result.messages, carry: result.pending?.results ?? [] });
      patch({
        text: result.text,
        sources: result.sources,
        pending: false,
        tool: null,
        ...(cards.length ? { actions: cards } : {}),
      });
    } catch (e) {
      const code: AiErrorCode = abort.signal.aborted
        ? 'network'
        : e instanceof AiError
          ? e.code
          : 'api';
      if (!(e instanceof AiError) && !abort.signal.aborted) console.error('ai', e);
      // The outcomes are not lost: they go along with the next question.
      set((s) => ({ carry: [...carry, ...s.carry] }));
      patch({ pending: false, tool: null, ...(abort.signal.aborted ? {} : { error: code }) });
    } finally {
      set({ running: false, abort: null });
    }
  };

  return {
    items: [],
    messages: [],
    carry: [],
    running: false,
    usage: null,
    abort: null,

    send: async (store, question, language) => {
      const text = question.trim();
      if (!text || get().running) return;
      await turn(store, language, text);
    },

    decide: async (store, callId, confirm, describe, language) => {
      const action = proposals.get(callId);
      const card = get()
        .items.flatMap((i) => i.actions ?? [])
        .find((a) => a.callId === callId);
      if (!action || !card || card.status !== 'pending') return;
      proposals.delete(callId);
      let result: ContentBlockParam;
      if (!confirm) {
        patchCard(callId, { status: 'declined' });
        result = outcome(callId, DECLINED);
      } else {
        patchCard(callId, { status: 'running' });
        try {
          const ran = await action.run();
          const activity = await store.addAiActivity({
            action: action.preview.action,
            summary: describe(action.preview),
            pageId: ran.pageId,
            undo: ran.undo,
          });
          undos.set(callId, { undo: ran.undo, activityId: activity.id });
          patchCard(callId, { status: 'done', activityId: activity.id });
          result = outcome(callId, `Done. ${ran.result}`);
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          patchCard(callId, { status: 'failed', error: message });
          result = outcome(callId, `Failed: ${message}`, true);
        }
      }
      set((s) => ({ carry: [...s.carry, result] }));

      // All cards of that answer decided, and Claude needs a result: go on now.
      const item = get().items.find((i) => i.actions?.some((a) => a.callId === callId));
      const cards = item?.actions ?? [];
      const open = cards.some((a) => a.status === 'pending' || a.status === 'running');
      const goOn = cards.some((a) => a.continueAfter && a.status === 'done');
      if (!open && goOn && !get().running) await turn(store, language, null);
    },

    undo: async (store, callId) => {
      const entry = undos.get(callId);
      if (!entry) return false;
      const ok = await undoAction(store, entry.undo);
      if (ok) {
        await store.markAiActivityUndone(entry.activityId);
        undos.delete(callId);
        patchCard(callId, { status: 'undone' });
      }
      return ok;
    },

    stop: () => get().abort?.abort(),

    reset: () => {
      get().abort?.abort();
      proposals.clear();
      set({ items: [], messages: [], carry: [], running: false, abort: null });
    },
  };
});
